import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, FormControl, IconButton, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useState } from 'react';
import { MINUTE_STEPS } from 'src/components/WeekSchedule/const';
import { ApplyToAllActions, ApplyToAllContainer, TimeWindowRow } from './styled';

type TimeWindow = { id: string; from: Dayjs; to: Dayjs };

export interface ApplyToAllDaysProps {
  onApply: (timeWindows: { from: Dayjs; to: Dayjs }[]) => void;
  hasActiveDays?: boolean;
  maxTimeWindows?: number;
}

const createTimeWindow = (): TimeWindow => ({
  id: crypto.randomUUID(),
  from: dayjs().hour(9).minute(0).second(0),
  to: dayjs().hour(17).minute(0).second(0),
});

export const ApplyToAllDays = ({
  onApply,
  hasActiveDays = true,
  maxTimeWindows,
}: ApplyToAllDaysProps) => {
  const [timeWindows, setTimeWindows] = useState<TimeWindow[]>([createTimeWindow()]);

  const addTimeWindow = useCallback(() => {
    setTimeWindows((prev) => [...prev, createTimeWindow()]);
  }, []);

  const removeTimeWindow = useCallback((index: number) => {
    setTimeWindows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateTimeWindow = useCallback(
    (index: number, field: 'from' | 'to', value: Dayjs | null) => {
      if (!value) return;
      setTimeWindows((prev) =>
        prev.map((tw, i) => (i === index ? { ...tw, [field]: value } : tw)),
      );
    },
    [],
  );

  const handleApply = useCallback(() => {
    const validWindows = timeWindows.filter((tw) => tw.from.isBefore(tw.to));
    if (validWindows.length === 0) return;
    onApply(validWindows);
  }, [timeWindows, onApply]);

  const hasValidTimeWindows = timeWindows.some((tw) => tw.from.isBefore(tw.to));
  const canApply = hasValidTimeWindows && hasActiveDays;
  const canAddMoreTimeWindows = !maxTimeWindows || timeWindows.length < maxTimeWindows;

  return (
    <ApplyToAllContainer>
      <Typography variant="body2">Apply to all:</Typography>

      {timeWindows.map((tw, index) => (
        <TimeWindowRow key={tw.id}>
          <FormControl>
            <TimePicker
              label="From"
              value={tw.from}
              onChange={(time) => updateTimeWindow(index, 'from', time)}
              timeSteps={{ minutes: MINUTE_STEPS }}
            />
          </FormControl>
          <Box sx={{ width: '20px', textAlign: 'center' }}>–</Box>
          <FormControl>
            <TimePicker
              label="To"
              value={tw.to}
              onChange={(time) => updateTimeWindow(index, 'to', time)}
              timeSteps={{ minutes: MINUTE_STEPS }}
            />
          </FormControl>
          {timeWindows.length > 1 && (
            <IconButton onClick={() => removeTimeWindow(index)} size="small">
              <DeleteIcon />
            </IconButton>
          )}
        </TimeWindowRow>
      ))}

      <ApplyToAllActions>
        {canAddMoreTimeWindows ? (
          <Button
            size="small"
            variant="text"
            startIcon={<AddCircleOutlineIcon />}
            onClick={addTimeWindow}
          >
            Add time window
          </Button>
        ) : null}
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={handleApply}
          disabled={!canApply}
        >
          Apply
        </Button>
      </ApplyToAllActions>
    </ApplyToAllContainer>
  );
};
